import type { RolOperacion } from '../dominio/contratos';

/**
 * CA-10.1 — operador y administrador son roles distintos a proposito.
 * Un operador de turno no cambia parametros de dispatch ni da de baja una
 * cuenta: son acciones con efecto sobre el ingreso y sobre la relacion
 * contractual con el conductor (docs/01 §3).
 */
export type Permiso =
  | 'ver.panel'
  | 'ver.mapa'
  | 'ver.viajes'
  | 'ver.conductores'
  | 'ver.suscripciones'
  | 'ver.incidentes'
  | 'revisar.documentos'
  | 'atender.incidentes'
  | 'suspender.conductor'
  | 'editar.dispatch'
  | 'editar.tarifario'
  /**
   * Publicar la POLITICA DE PRECIO (que modelo rige, y si la plataforma se compromete).
   *
   * ⚠️ **Permiso propio y no `editar.tarifario`, a proposito.** Son dos decisiones distintas: el
   * tarifario dice CUANTO sugerir; la politica dice SI ESO OBLIGA A ALGUIEN. Reusar el permiso del
   * tarifario haria que un nombre prometiera menos de lo que gobierna — el dia que alguien audite
   * "quien puede comprometer a la plataforma con un precio", la respuesta estaria escondida detras
   * de un permiso que dice otra cosa.
   */
  | 'editar.precio'
  /**
   * Publicar el PLAN de suscripción mensual del conductor (monto, intervalo, moneda).
   *
   * ⚠️ **Permiso propio, no `editar.tarifario` ni ningún otro, a propósito** — mismo criterio que
   * `editar.precio` (ver el comentario de arriba): reusar el permiso de otra pantalla haría que un
   * nombre prometiera menos de lo que gobierna. Esto decide cuánto se le cobra a un conductor cada
   * mes, no cuánto se le sugiere a un pasajero.
   */
  | 'editar.plan-suscripcion'
  | 'ver.auditoria';

const DEL_OPERADOR: readonly Permiso[] = [
  'ver.panel',
  'ver.mapa',
  'ver.viajes',
  'ver.conductores',
  'ver.suscripciones',
  'ver.incidentes',
  'revisar.documentos',
  'atender.incidentes',
];

/** El administrador hace todo lo del operador y ademas lo que toca ingreso y contrato. */
const DEL_ADMINISTRADOR: readonly Permiso[] = [
  ...DEL_OPERADOR,
  'suspender.conductor',
  'editar.dispatch',
  'editar.tarifario',
  'editar.precio',
  'editar.plan-suscripcion',
  'ver.auditoria',
];

const POR_ROL: Record<RolOperacion, readonly Permiso[]> = {
  operador: DEL_OPERADOR,
  administrador: DEL_ADMINISTRADOR,
};

export const puede = (rol: RolOperacion, permiso: Permiso): boolean =>
  POR_ROL[rol].includes(permiso);

/**
 * ⚠️ Esto oculta y bloquea en la INTERFAZ. No es el control.
 * El control real es del backend: si una accion mutante solo la impide esta
 * funcion, entonces cualquiera con la sesion abierta la ejecuta con un `fetch`.
 * Que este archivo exista no autoriza a que `apps/api` confie en el.
 */
export const ETIQUETA_ROL: Record<RolOperacion, string> = {
  operador: 'Operador',
  administrador: 'Administrador',
};
